import { useState, useEffect, useMemo } from 'react';
import { useChartColors } from '@/hooks/useChartColors';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { PieChart, Building2, Layers, LayoutGrid, Hash, Percent } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  Cell,
  ReferenceLine,
} from 'recharts';

interface AgencyUtilizationData {
  agencyName: string;
  agencyCode: string;
  HOME: number;
  PCP: number;
  URGENT_CARE: number;
  ER_NOW: number;
  total: number;
}

interface CareUtilizationMixChartProps {
  startDate?: Date;
  endDate?: Date;
  agencyId?: string;
  agencyName?: string | null;
}

import { RECOMMENDATION_COLORS } from '@/constants/chartColors';

// Service color palette using Recharts default colors
const SERVICE_PALETTE = {
  ER_NOW: RECOMMENDATION_COLORS.ER_NOW,
  PCP: RECOMMENDATION_COLORS.PCP,
  URGENT_CARE: RECOMMENDATION_COLORS.URGENT_CARE,
};

export default function CareUtilizationMixChart({ startDate, endDate, agencyId, agencyName }: CareUtilizationMixChartProps) {
  const chartColors = useChartColors();
  const isAllAgencies = !agencyId;
  const [data, setData] = useState<AgencyUtilizationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSessions, setTotalSessions] = useState(0);

  useEffect(() => {
    loadData();
  }, [startDate, endDate, agencyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get agencies - filter by agencyId if provided
      let agenciesQuery = supabase
        .from('agencies')
        .select('id, name, code')
        .eq('is_approved', true)
        .order('name', { ascending: true });

      if (agencyId) {
        agenciesQuery = agenciesQuery.eq('id', agencyId);
      }

      const { data: agencies, error: agenciesError } = await agenciesQuery;

      if (agenciesError) throw agenciesError;

      // Get triage sessions grouped by agency and recommendation
      let sessionsQuery = supabase
        .from('triage_sessions')
        .select('agency_id, recommendation')
        .not('completed_at', 'is', null)
        .not('recommendation', 'is', null);

      // Filter by agency if provided
      if (agencyId) {
        sessionsQuery = sessionsQuery.eq('agency_id', agencyId);
      }

      // Apply date filters if provided
      if (startDate) {
        sessionsQuery = sessionsQuery.gte('completed_at', startDate.toISOString());
      }
      if (endDate) {
        sessionsQuery = sessionsQuery.lte('completed_at', endDate.toISOString());
      }

      const { data: sessions, error: sessionsError } = await sessionsQuery;

      if (sessionsError) throw sessionsError;

      // Aggregate by agency
      const agencyData: Record<string, AgencyUtilizationData> = {};

      for (const agency of agencies || []) {
        // Extract short code (e.g., "CFHH" from "CFHH-01")
        const shortCode = agency.code.split('-')[0];
        agencyData[agency.id] = {
          agencyName: agency.name,
          agencyCode: shortCode,
          HOME: 0,
          PCP: 0,
          URGENT_CARE: 0,
          ER_NOW: 0,
          total: 0,
        };
      }

      let total = 0;
      for (const session of sessions || []) {
        if (session.agency_id && agencyData[session.agency_id]) {
          const rec = session.recommendation as keyof Pick<AgencyUtilizationData, 'HOME' | 'PCP' | 'URGENT_CARE' | 'ER_NOW'>;
          if (rec && ['HOME', 'PCP', 'URGENT_CARE', 'ER_NOW'].includes(rec)) {
            agencyData[session.agency_id][rec]++;
            agencyData[session.agency_id].total++;
            total++;
          }
        }
      }

      // Convert to array and filter out agencies with no sessions
      // Sort by agency code to match R order: CFHH, OHHC, FN, BSHC, CC
      const agencyOrder = ['CFHH', 'OHHC', 'FN', 'BSHC', 'CC'];
      const chartData = Object.values(agencyData)
        .filter(a => a.total > 0)
        .sort((a, b) => {
          const aIdx = agencyOrder.indexOf(a.agencyCode);
          const bIdx = agencyOrder.indexOf(b.agencyCode);
          if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
          if (aIdx >= 0) return -1;
          if (bIdx >= 0) return 1;
          return b.total - a.total;
        });

      setData(chartData);
      setTotalSessions(total);
    } catch (error) {
      console.error('Error loading care utilization data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Transform data to show normalized stacked values (like R code)
  const chartData = useMemo(() => {
    return data.map(d => {
      const totalServices = d.ER_NOW + d.PCP + d.URGENT_CARE;
      const erShare = totalServices > 0 ? d.ER_NOW / totalServices : 0;
      const pcpShare = totalServices > 0 ? d.PCP / totalServices : 0;
      const urgentShare = totalServices > 0 ? d.URGENT_CARE / totalServices : 0;
      
      return {
        ...d,
        // Stacked values normalized by total patients
        ER_NOW_stacked: d.total * erShare,
        PCP_stacked: d.total * pcpShare,
        URGENT_CARE_stacked: d.total * urgentShare,
        // Raw counts for labels
        ER_NOW_count: d.ER_NOW,
        PCP_count: d.PCP,
        URGENT_CARE_count: d.URGENT_CARE,
      };
    });
  }, [data]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    const agencyData = data.find(d => d.agencyCode === label);
    const total = agencyData?.total || 0;

    return (
      <div 
        className="rounded-lg shadow-lg p-3 text-sm border bg-white"
        style={chartColors.tooltipStyle}
      >
        <p className="font-semibold mb-2 text-gray-900">{agencyData?.agencyName || label}</p>
        {payload.map((entry: any, index: number) => {
          const rawKey = entry.dataKey.replace('_stacked', '') as keyof Pick<AgencyUtilizationData, 'ER_NOW' | 'PCP' | 'URGENT_CARE'>;
          const absoluteValue = agencyData?.[rawKey] || 0;
          
          const labels: Record<string, string> = {
            ER_NOW: 'Emergency Care (ER)',
            PCP: 'Same-day PCP',
            URGENT_CARE: 'Urgent Care'
          };
          
          return (
            <div key={index} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: entry.fill }}
                />
                <span className="text-gray-600">{labels[rawKey]}:</span>
              </div>
              <span className="font-medium text-gray-900">
                {absoluteValue.toLocaleString()}
              </span>
            </div>
          );
        })}
        <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between">
          <span className="text-gray-600">Total Patients:</span>
          <span className="font-bold text-gray-900">{total.toLocaleString()}</span>
        </div>
      </div>
    );
  };

  // Custom label component for showing counts inside bars
  const renderCustomizedLabel = (props: any) => {
    const { x, y, width, height, value, dataKey } = props;
    
    if (!value || value < 30) return null; // Don't show label if bar is too small
    
    const countKey = dataKey.replace('_stacked', '_count');
    const actualCount = chartData.find(d => d[dataKey] === value)?.[countKey];
    
    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        fill="white"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={11}
        fontFamily="Georgia, serif"
        fontWeight="500"
      >
        {actualCount?.toLocaleString()}
      </text>
    );
  };

  if (loading) {
    return (
      <Card className={`${chartColors.cardClassName}`}>
        <CardHeader>
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${chartColors.cardClassName} shadow-sm`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <PieChart className="h-5 w-5 text-blue-700" />
            </div>
            <div>
              <CardTitle className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
                Care Utilization Mix by Agency (2025)
              </CardTitle>
              <CardDescription className="text-gray-600">
                Total patients stacked by normalized service mix
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="gap-1 bg-white border-gray-300 text-gray-700">
              <Building2 className="h-3 w-3" />
              {isAllAgencies ? 'All Agencies' : agencyName || 'Agency'}
            </Badge>
            <Badge variant="outline" className="gap-1 bg-white border-gray-300 text-gray-700">
              {data.length} {isAllAgencies ? 'agencies' : 'agency'}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {data.length === 0 ? (
          <div className="flex items-center justify-center h-[400px] text-gray-500">
            No data available
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={chartData}
              margin={{ top: 40, right: 30, left: 40, bottom: 40 }}
              barCategoryGap="28%"
            >
              <CartesianGrid strokeDasharray="0" stroke={chartColors.grid} vertical={false} />
              <XAxis
                dataKey="agencyCode"
                tick={{ fontSize: 12, fontFamily: 'Georgia, serif', fill: chartColors.axis }}
                tickLine={false}
                axisLine={{ stroke: chartColors.axis, strokeWidth: 1 }}
              />
              <YAxis
                tick={{ fontSize: 11, fontFamily: 'Georgia, serif', fill: chartColors.axis }}
                tickLine={false}
                axisLine={{ stroke: chartColors.axis, strokeWidth: 1 }}
                tickFormatter={(value) => value.toLocaleString()}
                label={{
                  value: 'Total patients (stacked by normalized mix)',
                  angle: -90,
                  position: 'insideLeft',
                  offset: 10,
                  style: { 
                    textAnchor: 'middle', 
                    fontSize: 11, 
                    fontFamily: 'Georgia, serif',
                    fontWeight: 'bold',
                    fill: chartColors.axis
                  }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="top"
                align="right"
                wrapperStyle={{ 
                  paddingBottom: 10,
                  fontFamily: 'Georgia, serif',
                  fontSize: 11
                }}
                formatter={(value) => {
                  const labels: Record<string, string> = {
                    ER_NOW_stacked: 'Emergency Care (ER)',
                    PCP_stacked: 'Same-day PCP',
                    URGENT_CARE_stacked: 'Urgent Care'
                  };
                  return <span style={{ color: '#374151' }}>{labels[value] || value}</span>;
                }}
              />
              
              {/* Stacked Bars - order from bottom: ER, PCP, Urgent Care */}
              <Bar 
                dataKey="ER_NOW_stacked" 
                name="ER_NOW_stacked"
                stackId="a" 
                fill="#EF4444"
                radius={[0, 0, 0, 0]}
                animationDuration={1200}
                animationEasing="ease-out"
              >
                <LabelList 
                  dataKey="ER_NOW_count" 
                  position="center" 
                  fill="white"
                  fontSize={11}
                  fontFamily="Georgia, serif"
                  formatter={(value: number) => value > 30 ? value.toLocaleString() : ''}
                />
              </Bar>
              <Bar 
                dataKey="PCP_stacked" 
                name="PCP_stacked"
                stackId="a" 
                fill="#10B981"
                animationDuration={1200}
                animationEasing="ease-out"
                animationBegin={200}
              >
                <LabelList 
                  dataKey="PCP_count" 
                  position="center" 
                  fill="white"
                  fontSize={11}
                  fontFamily="Georgia, serif"
                  formatter={(value: number) => value > 30 ? value.toLocaleString() : ''}
                />
              </Bar>
              <Bar 
                dataKey="URGENT_CARE_stacked" 
                name="URGENT_CARE_stacked"
                stackId="a" 
                fill="#F59E0B"
                radius={[4, 4, 0, 0]}
                animationDuration={1200}
                animationEasing="ease-out"
                animationBegin={400}
              >
                <LabelList 
                  dataKey="URGENT_CARE_count" 
                  position="center" 
                  fill="white"
                  fontSize={11}
                  fontFamily="Georgia, serif"
                  formatter={(value: number) => value > 20 ? value.toLocaleString() : ''}
                />
                {/* Total label on top of bars */}
                <LabelList 
                  dataKey="total" 
                  position="top" 
                  fill="#374151"
                  fontSize={11}
                  fontFamily="Georgia, serif"
                  fontWeight="500"
                  formatter={(value: number) => value.toLocaleString()}
                  offset={8}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-border">
          <div className="text-center">
            <p className="text-lg font-bold" style={{ color: SERVICE_PALETTE.ER_NOW }}>
              {data.reduce((sum, d) => sum + d.ER_NOW, 0).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Emergency Care (ER)</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold" style={{ color: SERVICE_PALETTE.PCP }}>
              {data.reduce((sum, d) => sum + d.PCP, 0).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Same-day PCP</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold" style={{ color: SERVICE_PALETTE.URGENT_CARE }}>
              {data.reduce((sum, d) => sum + d.URGENT_CARE, 0).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Urgent Care</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}